import { c as _c } from "react/compiler-runtime";
import React from "react";
import CircleLoader, { CircleLoaderSize } from "../distribution-plan-tool/common/CircleLoader";
import { faFile, faFileAudio, faFileCsv, faFilePdf, faFileVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFileExtension } from "./memes/file-upload/utils/formatHelpers";
interface FileItem {
  file: File;
  label: string | null;
}
interface UploadingFile {
  file: File;
  isUploading: boolean;
  progress: number;
}
interface FilePreviewProps {
  readonly files: FileItem[];
  readonly uploadingFiles: UploadingFile[];
  readonly removeFile: (file: File) => void;
  readonly disabled: boolean;
}
const ProgressOverlay: React.FC<{
  progress: number;
}> = t0 => {
  const $ = _c(2);
  const {
    progress
  } = t0;
  const t1 = `inset(0 0 0 ${progress}%)`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className="tw-absolute tw-inset-0 tw-bg-black tw-opacity-60 tw-transition-all tw-duration-300 tw-ease-out" style={{
      clipPath: t1
    }} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
};
const getFileIcon = (file: File) => {
  if (file.type.startsWith("video/")) {
    return faFileVideo;
  }
  if (file.type.startsWith("audio/")) {
    return faFileAudio;
  }
  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
    return faFilePdf;
  }
  if (file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv")) {
    return faFileCsv;
  }
  return faFile;
};
const FileTypePreview: React.FC<{
  file: File;
}> = t0 => {
  const $ = _c(12);
  const {
    file
  } = t0;
  let t1;
  if ($[0] !== file) {
    t1 = getFileExtension(file, "FILE");
    $[0] = file;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${t1} file: ${file.name}`;
  let t3;
  if ($[2] !== file) {
    t3 = getFileIcon(file);
    $[2] = file;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <FontAwesomeIcon icon={t3} className="tw-size-7" />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== file.name) {
    t5 = <div title={file.name} className="tw-absolute tw-bottom-2 tw-left-3 tw-right-3 tw-truncate tw-text-center tw-text-[11px] tw-text-iron-500">{file.name}</div>;
    $[6] = file.name;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t4 || $[10] !== t5) {
    t6 = <div role="img" className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-px-3 tw-text-iron-300" aria-label={t2}>{t4}{t5}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
const FilePreview: React.FC<FilePreviewProps> = t0 => {
  const $ = _c(11);
  const {
    files,
    uploadingFiles,
    removeFile,
    disabled
  } = t0;
  let t1;
  if ($[0] !== disabled || $[1] !== files || $[2] !== removeFile || $[3] !== uploadingFiles) {
    let t2;
    if ($[5] !== disabled || $[6] !== removeFile || $[7] !== uploadingFiles) {
      t2 = (file, index) => {
        const uploadingFile = uploadingFiles.find(uf => uf.file === file.file);
        const isUploading = !!uploadingFile;
        const progress = uploadingFile?.progress ?? 0;
        const fileKey = `${file.file.name}-${file.file.size}-${file.file.lastModified}-${index}`;
        return <div key={fileKey} className="tw-group tw-relative"><div className="tw-size-24 tw-overflow-hidden tw-rounded-lg tw-bg-iron-800">{file.file.type.startsWith("image/") ? <div className="tw-relative tw-h-full tw-w-full"><img src={URL.createObjectURL(file.file)} alt={`Preview ${index}`} className="tw-h-full tw-w-full tw-object-cover" /><div className="tw-absolute tw-inset-0 tw-bg-iron-950 tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-30" /></div> : <FileTypePreview file={file.file} />}{isUploading && <><ProgressOverlay progress={progress} /><div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center"><CircleLoader size={CircleLoaderSize.XXLARGE} /><span className="tw-mt-1 tw-text-base tw-font-medium tw-text-white">{Math.round(progress)}%</span></div></>}</div>{file.label && <div className="tw-absolute tw-left-2 tw-right-10 tw-top-2 tw-truncate tw-rounded tw-bg-iron-800/80 tw-px-2 tw-py-1 tw-text-sm tw-text-white">{file.label}</div>}{!isUploading && <button onClick={() => removeFile(file.file)} disabled={disabled} className={`tw-absolute tw-right-1 tw-top-1 tw-z-10 tw-flex tw-size-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-iron-800 tw-text-red tw-transition-all tw-duration-300 hover:tw-bg-iron-700 ${disabled ? "tw-pointer-events-none" : ""}`} aria-label="Remove file"><svg className="tw-size-5 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg></button>}</div>;
      };
      $[5] = disabled;
      $[6] = removeFile;
      $[7] = uploadingFiles;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = files.map(t2);
    $[0] = disabled;
    $[1] = files;
    $[2] = removeFile;
    $[3] = uploadingFiles;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2">{t1}</div>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
export default FilePreview;